import React, { useState, useEffect, useRef } from "react";
import axios from "../utils/axiosInstance";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../layouts/AuthLayout";

const API_URL = process.env.REACT_APP_API_URL;

/* --------------------------------------------------
   Role messaging (UNCHANGED)
-------------------------------------------------- */
const roleMessages = {
  founder:
    "Build meaningful products with clarity, confidence, and AI-guided direction.",
  investor:
    "Discover high-potential founders and ideas early in their journey.",
  mentor:
    "Guide founders, share expertise, and grow your professional influence.",
};

const EMPTY_FORM = {
  name: "",
  email: "",
  mobile: "",
  country: "",
  city: "",
  password: "",
  role: "",
};

const Register = () => {
  const navigate = useNavigate();
  console.log("API URL TEST BUILD:", API_URL);
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();

  const isProUpgrade = searchParams.get("plan") === "pro";
  const isLiteFlow = searchParams.get("source") === "lite";
  const liteResumeStep = localStorage.getItem("lite_resume_step");

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [roleHint, setRoleHint] = useState("");
  const [agree, setAgree] = useState(false);

  // 🔐 refs to hard-reset autofill
  const formRef = useRef(null);

  /* --------------------------------------------------
     🔒 HARD SECURITY RESET (CRITICAL)
  -------------------------------------------------- */
  useEffect(() => {
    // Reset React state
    setForm(EMPTY_FORM);
    setError("");
    setLoading(false);
    setRoleHint("");
    setAgree(false);

    // Lite flow must be anonymous
    if (isLiteFlow) {
      logout?.();
      localStorage.removeItem("user");
      sessionStorage.clear();
    }

    // Pro upgrade keeps behaviour unchanged
    if (isProUpgrade) {
      logout?.();
      localStorage.removeItem("user");
    }

    // 🔥 Kill browser autofill AFTER render
    setTimeout(() => {
      if (formRef.current) {
        const inputs = formRef.current.querySelectorAll("input");
        inputs.forEach((input) => {
          input.value = "";
        });
      }
    }, 50);
  }, [isLiteFlow, isProUpgrade, logout]);

  /* --------------------------------------------------
     Best-effort location (UNCHANGED)
  -------------------------------------------------- */
  useEffect(() => {
    axios
      .get("/api/profile")
      .then((res) => {
        if (res.data) {
          setForm((prev) => ({
            ...prev,
            country: res.data.country || "",
            city: res.data.city || "",
          }));
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });

    if (error) setError(""); // ✅ clear error when user types
    };

  const handleRoleSelect = (role) => {
    setForm((prev) => ({ ...prev, role }));
    setRoleHint(roleMessages[role]);
    setError("");
  };

  /* --------------------------------------------------
     Email signup (CORE LOGIC UNCHANGED)
  -------------------------------------------------- */
  const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  const liteIntent = localStorage.getItem("lite_intent");
  const liteSessionId = localStorage.getItem("lite_session_id");

       if (!form.role) {
        setError("Please choose how you want to use MDIH.");
        return;
      }

      if (!agree) {
        setError("Please accept Terms & Privacy Policy to continue");
        return;
      }

    // ✅ Email validation (NEW)
      const cleanEmail = form.email.trim().toLowerCase();

      if (!isValidEmail(cleanEmail)) {
        setError("Enter a valid email to save your startup journey (e.g. name@gmail.com)");
        return;
      }

      // optional: block obvious junk
      const blocked = ["test", "fake", "abc", "123"];
      if (blocked.some(p => cleanEmail.includes(p))) {
        setError("Please enter a real email address");
        return;
      }

    setLoading(true);
    setError("");

    try {
      const payload = {
        ...form,
        email: cleanEmail, // ✅ normalized email
        liteIntent: liteIntent || null, // ✅ NEW
        liteSessionId: liteSessionId || null, // ✅ ADD THIS
        status: form.role === "mentor" ? "pending" : undefined,
        plan: "free",
      };

      

      const res = await axios.post(
        `/api/auth/signup`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      // ✅ Google Analytics event
        if (window.gtag) {
          window.gtag('event', 'signup', {
            method: 'email',
          });
        }

      alert(res.data.message || "Registration successful!");
      


      // ✅ Mark Lite registration explicitly
      if (isLiteFlow) {
        localStorage.setItem("lite_registration_confirmed", "true");
      }

      if (isProUpgrade) return navigate("/payment");

      if (isLiteFlow && liteResumeStep === "market-potential") {
        setTimeout(() => {
          navigate("/lite/market-potential");
        }, 0);
        return;
      }

      if (form.role === "founder") {
          // ✅ CRITICAL FIX
          localStorage.setItem("lite_entry_decided", "true");

          return navigate("/lite/welcome");
        }

      navigate("/login");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    alert(
      "Google Sign-In coming soon.\n\nYour role will be selected after sign-in."
    );
  };

  return (
    <AuthLayout
      key="register-lite-secure" // 🔑 force remount
      title="Create your MDIH account"
      subtitle="Start your guided innovation journey. Free to begin."
    >
      {/* ---------- GOOGLE SIGN-UP ---------- */}
      <button
        onClick={handleGoogleSignup}
        className="w-full border rounded-md py-3 text-sm font-medium hover:bg-gray-50 transition"
      >
        Continue with Google
      </button>

      <p className="text-xs text-gray-500 text-center mt-2">
        Role selection happens after sign-in
      </p>

      {/* ---------- DIVIDER ---------- */}
      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400">or sign up with email</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* ---------- ROLE SELECTION ---------- */}
      <div className="mb-5">
        <p className="text-sm font-medium text-gray-700 mb-2">
          Choose how you’ll use MDIH
        </p>

        <div className="flex gap-2">
          {["founder", "investor", "mentor"].map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => handleRoleSelect(role)}
              className={`flex-1 text-sm py-2 rounded-md border transition ${
                form.role === role
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </button>
          ))}
        </div>

        {roleHint && (
          <p className="text-xs text-gray-500 mt-2">{roleHint}</p>
        )}
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* ---------- FORM ---------- */}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="space-y-3"
        autoComplete="off"
      >
        <input
          name="name"
          autoComplete="off"
          placeholder="Full name"
          className="w-full border rounded-md px-3 py-2.5 text-sm"
          value={form.name}
          onChange={handleChange}
          required
        />

        <input
          name="email"
          type="email"
          autoComplete="new-email"
          placeholder="Email address"
          className="w-full border rounded-md px-3 py-2.5 text-sm"
          value={form.email}
          onChange={handleChange}
          required
        />

        <input
          name="mobile"
          autoComplete="off"
          placeholder="Mobile number"
          className="w-full border rounded-md px-3 py-2.5 text-sm"
          value={form.mobile}
          onChange={handleChange}
          required
        />

        <div className="flex gap-2">
          <input
            name="country"
            autoComplete="off"
            placeholder="Country"
            className="w-full border rounded-md px-3 py-2.5 text-sm"
            value={form.country}
            onChange={handleChange}
            required
          />
          <input
            name="city"
            autoComplete="off"
            placeholder="City"
            className="w-full border rounded-md px-3 py-2.5 text-sm"
            value={form.city}
            onChange={handleChange}
            required
          />
        </div>

        <input
          name="password"
          type="password"
          autoComplete="new-password"
          placeholder="Password (min 6 characters)"
          minLength={6}
          className="w-full border rounded-md px-3 py-2.5 text-sm"
          value={form.password}
          onChange={handleChange}
          required
        />

          {/* ✅ Terms & Privacy */}
          <div className="flex items-start gap-2 text-xs text-gray-600 mt-2">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-1"
            />
            <span>
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                Terms
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                Privacy Policy
              </a>
            </span>
          </div>
        <button
          type="submit"
          disabled={loading || !agree}
          className={`w-full py-2.5 rounded-md text-sm font-medium text-white transition ${
            loading
              ? "bg-blue-400"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {loading ? "Creating account…" : "Start free journey"}
        </button>
      </form>

      {/* ---------- LOGIN ---------- */}
      <p className="text-sm text-gray-600 text-center mt-5">
        Already have an account?{" "}
        <button
          onClick={() => navigate("/login?source=lite")}
          className="text-blue-600 hover:underline font-medium"
        >
          Sign in
        </button>
      </p>
    </AuthLayout>
  );
};

export default Register;
